import { action, computed, makeObservable, observable } from "mobx";
import { useEffect } from "react";
import { SetURLSearchParams, useSearchParams } from "react-router-dom";
import { AppSubStore, AppSubStoreArgs } from "@/store/types";
import {
  AGENT_ACTIVITY_ID_SEARCH_PARAM,
  AGENT_PROJECT_ID_SEARCH_PARAM,
  AGENT_SEARCH_PARAM,
  AGENT_TASK_ID_SEARCH_PARAM,
  AgentOverlayState,
  AgentSurfaceId,
} from "@/store/agent-overlay/types";

export class AgentOverlayStore extends AppSubStore {
  /**
   * The validated state from the `?agent=` query params. This is the source of
   * truth for whether the full-screen agent surface is open.
   */
  agentParam: AgentOverlayState | null = null;

  private setSearchParams: SetURLSearchParams | null = null;

  constructor(injectedDeps: AppSubStoreArgs) {
    super(injectedDeps);

    makeObservable<AgentOverlayStore, "setSearchParams" | "updateAgentParams" | "parseAgentState">(
      this,
      {
        agentParam: observable,
        setSearchParams: observable,
        isOpen: computed,
        activeSurface: computed,
        openOverview: action,
        openProjects: action,
        openProject: action,
        openTasks: action,
        openTask: action,
        openActivities: action,
        openActivity: action,
        close: action,
        setAgentParam: action,
        updateAgentParams: false,
        parseAgentState: false,
        useSyncAgentSearchParam: action,
      }
    );
  }

  get isOpen(): boolean {
    return this.agentParam !== null;
  }

  get activeSurface(): AgentOverlayState {
    return this.agentParam ?? { surfaceId: AgentSurfaceId.Overview };
  }

  openOverview() {
    this.updateAgentParams({ surfaceId: AgentSurfaceId.Overview });
  }

  openProjects() {
    this.updateAgentParams({ surfaceId: AgentSurfaceId.Projects });
  }

  openProject({ projectId }: { projectId: string }) {
    if (!projectId) return;

    this.updateAgentParams({
      surfaceId: AgentSurfaceId.Project,
      projectId,
    });
  }

  openTasks() {
    this.updateAgentParams({ surfaceId: AgentSurfaceId.Tasks });
  }

  openTask({ taskId }: { taskId: string }) {
    if (!taskId) return;

    this.updateAgentParams({
      surfaceId: AgentSurfaceId.Task,
      taskId,
    });
  }

  openActivities() {
    this.updateAgentParams({ surfaceId: AgentSurfaceId.Activities });
  }

  openActivity({ activityId }: { activityId: string }) {
    if (!activityId) return;

    this.updateAgentParams({
      surfaceId: AgentSurfaceId.Activity,
      activityId,
    });
  }

  close() {
    this.updateAgentParams(null);
  }

  setAgentParam(agentParam: AgentOverlayState | null) {
    this.agentParam = agentParam;
  }

  private updateAgentParams(agentState: AgentOverlayState | null) {
    if (!this.setSearchParams) return;

    this.setSearchParams(
      prevParams => {
        prevParams.delete(AGENT_SEARCH_PARAM);
        prevParams.delete(AGENT_PROJECT_ID_SEARCH_PARAM);
        prevParams.delete(AGENT_TASK_ID_SEARCH_PARAM);
        prevParams.delete(AGENT_ACTIVITY_ID_SEARCH_PARAM);

        if (!agentState) {
          return prevParams;
        }

        prevParams.delete("settings");
        prevParams.set(AGENT_SEARCH_PARAM, agentState.surfaceId);

        if (agentState.surfaceId === AgentSurfaceId.Project) {
          prevParams.set(AGENT_PROJECT_ID_SEARCH_PARAM, agentState.projectId);
        }

        if (agentState.surfaceId === AgentSurfaceId.Task) {
          prevParams.set(AGENT_TASK_ID_SEARCH_PARAM, agentState.taskId);
        }

        if (agentState.surfaceId === AgentSurfaceId.Activity) {
          prevParams.set(AGENT_ACTIVITY_ID_SEARCH_PARAM, agentState.activityId);
        }

        return prevParams;
      },
      { replace: true }
    );
  }

  private parseAgentState({
    rawAgentParam,
    projectId,
    taskId,
    activityId,
  }: {
    rawAgentParam: string | null;
    projectId: string | null;
    taskId: string | null;
    activityId: string | null;
  }): AgentOverlayState | null {
    switch (rawAgentParam) {
      case AgentSurfaceId.Overview:
        return { surfaceId: AgentSurfaceId.Overview };
      case AgentSurfaceId.Projects:
        return { surfaceId: AgentSurfaceId.Projects };
      case AgentSurfaceId.Project:
        return projectId ? { surfaceId: AgentSurfaceId.Project, projectId } : null;
      case AgentSurfaceId.Tasks:
        return { surfaceId: AgentSurfaceId.Tasks };
      case AgentSurfaceId.Task:
        return taskId ? { surfaceId: AgentSurfaceId.Task, taskId } : null;
      case AgentSurfaceId.Activities:
        return { surfaceId: AgentSurfaceId.Activities };
      case AgentSurfaceId.Activity:
        return activityId ? { surfaceId: AgentSurfaceId.Activity, activityId } : null;
      default:
        return null;
    }
  }

  /**
   * Syncs the `?agent=<surface-id>` query params into this store.
   *
   * Called from `AppStore.useInitializeAppStoreEffects()`.
   */
  useSyncAgentSearchParam() {
    const [searchParams, setSearchParams] = useSearchParams();
    this.setSearchParams = setSearchParams;

    const rawAgentParam = searchParams.get(AGENT_SEARCH_PARAM);
    const projectId = searchParams.get(AGENT_PROJECT_ID_SEARCH_PARAM);
    const taskId = searchParams.get(AGENT_TASK_ID_SEARCH_PARAM);
    const activityId = searchParams.get(AGENT_ACTIVITY_ID_SEARCH_PARAM);

    useEffect(() => {
      if (!rawAgentParam) {
        this.setAgentParam(null);
        return;
      }

      const agentState = this.parseAgentState({
        rawAgentParam,
        projectId,
        taskId,
        activityId,
      });

      if (agentState) {
        this.setAgentParam(agentState);
        return;
      }

      this.setAgentParam(null);
      this.updateAgentParams(null);
    }, [rawAgentParam, projectId, taskId, activityId]);
  }
}
